import { useCallback, useState } from "react";
import useTabStore from "@/stores/useTabStore";
import type { WidgetSearchData } from "@/utils/DashboardLayouts";

const EMPTY: WidgetSearchData = {};

/**
 * Manages a widget's persisted search params (query + date range), mirroring
 * `useWidgetFilterState`.
 *
 * When `persistKey` is provided, reads/writes from
 * `DashboardTab.widgetFilters[persistKey].search` in `useTabStore` (server-synced),
 * so a search stays "in play" across Done, tab switches, and reloads. When
 * `persistKey` is undefined (e.g. popout / standalone widget), falls back to local
 * component state.
 */
export function useWidgetSearchState(
  persistKey: string | undefined
): [WidgetSearchData, (value: WidgetSearchData) => void] {
  // Fallback: local state for widgets rendered outside a tab (no persistKey)
  const [localSearch, setLocalSearch] = useState<WidgetSearchData>(EMPTY);

  const storeSearch = useTabStore(
    useCallback(
      (state) => {
        if (!persistKey) return EMPTY;
        const tab = state.tabs.find((t) => t.id === state.activeTabId);
        return tab?.widgetFilters?.[persistKey]?.search ?? EMPTY;
      },
      [persistKey]
    )
  );

  const setStoreSearch = useCallback(
    (value: WidgetSearchData) => {
      if (!persistKey) return;
      useTabStore.getState().setWidgetSearch(persistKey, value);
    },
    [persistKey]
  );

  if (!persistKey) {
    return [localSearch, setLocalSearch];
  }

  return [storeSearch, setStoreSearch];
}
